import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label } from '@1trading/react';
import { Banknote } from 'lucide-react';
import { useId, useState } from 'react';
import { toast } from 'sonner';
import { CoinCell } from '@/components/ui/coin-cell';
import { buildUpdateLoanRequest, type ClientLoan, isValidQuantity, updateLoan } from '@/services/api/loans';
import { runAuditedLoanMutation } from './run-audited-loan-mutation';

const CURRENCY_ICON_SIZE = 32;
const QUANTITY_ERROR = 'Enter a valid amount like 123 or 123.45';

export type CreditLineDialogMode = 'update' | 'delete';

interface CreditLineDialogProps {
  client: ClientLoan;
  currency: string;
  mode: CreditLineDialogMode;
  onClose: () => void;
  /** Refetch loans after a successful mutation (full-state upsert — always rebuild from fresh data) */
  onMutated: () => void;
  /** HTTP 403 → the page-level Unauthorised Action dialog */
  onUnauthorised: () => void;
}

// Edit and delete share one dialog, exactly like legacy BopLoansCard.
// Mounted only while open, so state resets on every open.
export const CreditLineDialog = ({ client, currency, mode, onClose, onMutated, onUnauthorised }: CreditLineDialogProps) => {
  const fieldId = useId();
  const currentQuantity = client.loans[currency]?.quantity ?? 0;
  const [quantity, setQuantity] = useState(() => String(currentQuantity));
  const isDelete = mode === 'delete';
  // Zero is deliberately allowed on update (the server books the decrease)
  const isQuantityValid = isValidQuantity(quantity, { allowZero: true });

  const handleUpdate = async () => {
    if (!isQuantityValid) return;
    const trimmed = quantity.trim();
    const outcome = await runAuditedLoanMutation({
      title: `Confirm Update ${currency} loan amount for ${client.clientName}`,
      description: `Proceeding with this action will update the ${currency} credit line amount for ${client.clientName} to ${trimmed}. Your action here will be logged and will be auditable.`,
      fallbackError: 'Failed to update credit line',
      execute: (comment) => updateLoan(buildUpdateLoanRequest(client, { kind: 'set', currency, quantity: Number.parseFloat(trimmed) }, comment)),
    });
    if (outcome === 'unauthorised') {
      onClose();
      onUnauthorised();
      return;
    }
    if (outcome !== 'success') return;
    toast.success(`Successfully updated ${currency} credit line for ${client.clientName}`);
    onMutated();
    onClose();
  };

  const handleDelete = async () => {
    const outcome = await runAuditedLoanMutation({
      title: `Confirm Delete ${currency} loan for ${client.clientName}`,
      description: `Proceeding with this action will delete the entire ${currency} credit line for ${client.clientName}. Your action here will be logged and will be auditable.`,
      destructive: true,
      // Deletion books an outgoing ADJUSTMENT for the full amount — the most
      // likely failure here is INSUFFICIENT_FUNDS, mapped to a clear message.
      fallbackError: 'Failed to update credit line',
      execute: (comment) => updateLoan(buildUpdateLoanRequest(client, { kind: 'remove', currency }, comment)),
    });
    if (outcome === 'unauthorised') {
      onClose();
      onUnauthorised();
      return;
    }
    if (outcome !== 'success') return;
    toast.success(`Successfully deleted ${currency} credit line for ${client.clientName}`);
    onMutated();
    onClose();
  };

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="size-5" />
            {isDelete ? `Delete ${currency} credit line for ${client.clientName}` : `Update credit line of ${currency} for ${client.clientName}`}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {isDelete ? <p className="text-sm">Do you wish to delete the following credit line?</p> : null}
          <CoinCell asset={currency} size={CURRENCY_ICON_SIZE} />
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-quantity`}>Credit line Quantity</Label>
            <Input disabled={isDelete} id={`${fieldId}-quantity`} inputMode="decimal" onChange={(event) => setQuantity(event.target.value)} value={quantity} />
            {!isDelete && !isQuantityValid ? <p className="text-sm text-red-500">{QUANTITY_ERROR}</p> : null}
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose} type="button" variant="outline">
            Cancel
          </Button>
          {isDelete ? (
            <Button onClick={handleDelete} type="button" variant="destructive">
              Delete Credit Line
            </Button>
          ) : (
            // Fix: Save is gated on a strict decimal check instead of the
            // legacy [0-9.] char whitelist that let '', '.', '1.2.3' through
            <Button disabled={!isQuantityValid} onClick={handleUpdate} type="button" variant="primary">
              Save changes
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
CreditLineDialog.displayName = 'CreditLineDialog';
